// sim/geneLootScaling.ts — RELATIVE LOOT GENERATION: forge a reward's power as a
// rarity-banded window around the player's CURRENT power, so loot reads as incremental
// progression instead of a lottery jackpot.
//
// THE PROBLEM. Reward power used to be generated independently of the player: a gene's
// Power Score is dominated by a flat per-rarity floor (genePower POWER_RARITY_STEP), so a
// Legendary always lands ~the same absolute number no matter what you already hold. Early
// on that is a 300–500% spike; once you out-scale it, it is junk. Either way it ignores
// where the player actually is.
//
// THE MODEL. Take a BASELINE B — the power of the gene you currently rely on for that slot
// (with caste / colony gene-power as the fallback when the slot is empty). A reward of a
// given rarity is then generated to land in a multiplicative WINDOW of B, the window
// sliding upward with rarity:
//
//     Common 0.95–1.05 · Uncommon 1.025–1.125 · Rare 1.075–1.20 · Epic 1.125–1.30 ·
//     Legendary 1.20–1.50 · (Apex 1.40–1.85)
//
// So at B=200 a Common is ~190–210 (a sidegrade), a Legendary ~240–300 (a solid but not
// absurd step). Every reward is an increment over what you have; rarity sets how big.
//
// PURE + DETERMINISTIC: integer-friendly window math, no RNG, no sim mutation, no DOM. The
// forge (web/evolutionBox.ts) draws the in-window POSITION and fits the gene to the target;
// this module only computes the window and the target. Headlessly tested.

import { RARITY } from '../types.ts';

/**
 * [loRatio, hiRatio] multipliers of the baseline power, by rarity. Indices are RARITY.*
 * (Common…Legendary); index 5 ("Apex") covers ascended / fused / miracle-tier rewards.
 * Windows overlap and climb so progression is continuous rather than stepped.
 */
export const REWARD_RATIO_BAND: readonly (readonly [number, number])[] = [
  [0.95, 1.05],   // Common    — a sidegrade
  [1.025, 1.125], // Uncommon
  [1.075, 1.20],  // Rare
  [1.125, 1.30],  // Epic
  [1.20, 1.50],   // Legendary — a solid step, not a jackpot
  [1.40, 1.85],   // Apex
];

/** Effective-tier index of the top "Apex" band. */
export const APEX_TIER = REWARD_RATIO_BAND.length - 1;

/**
 * Baseline used when the player holds NO comparable gene yet (a fresh slot with no
 * caste/colony gene-power to lean on). Chosen so a first gene reads as a real but modest
 * find in the same Power-Score scale as a starter gene; real baselines only climb from here.
 */
export const STARTER_BASELINE = 120;

/** The [lo, hi] ratio band for a rarity (clamped to the table). */
export function ratioBand(rarity: number): readonly [number, number] {
  const r = rarity < 0 ? 0 : rarity >= REWARD_RATIO_BAND.length ? REWARD_RATIO_BAND.length - 1 : rarity | 0;
  return REWARD_RATIO_BAND[r] as readonly [number, number];
}

export interface RewardWindow { lo: number; hi: number; }

/**
 * The integer Power-Score window a reward of `rarity` should land in, given the player's
 * current `baseline` power for that slot. A non-positive baseline falls back to
 * STARTER_BASELINE so the window is always real and positive.
 */
export function rewardWindow(baseline: number, rarity: number): RewardWindow {
  const b = baseline > 0 ? baseline : STARTER_BASELINE;
  const [lo, hi] = ratioBand(rarity);
  return { lo: Math.round(b * lo), hi: Math.round(b * hi) };
}

/**
 * The target Power Score inside the rarity window, `position` (0..1) sliding from the
 * window floor to its ceiling. The forge draws `position` from its per-open RNG and fits
 * the gene's trait magnitude to this target. Deterministic; clamps `position` to [0,1].
 */
export function targetRewardPower(baseline: number, rarity: number, position: number): number {
  const { lo, hi } = rewardWindow(baseline, rarity);
  const p = position < 0 ? 0 : position > 1 ? 1 : position;
  return Math.round(lo + p * (hi - lo));
}

/**
 * Blend the three progression baselines into the per-slot baseline the forge uses:
 *  - `equipped` — power of the gene you currently hold in this slot (−1 / 0 = none);
 *  - `casteGene` — your caste's average held-gene power (the "Current Caste Power" signal);
 *  - `colonyGene` — your colony-wide average held-gene power ("Current Colony Power").
 *
 * The equipped gene is the truest baseline (it is exactly what a reward must beat); when
 * the slot is empty we fall back to the caste level, then the colony level, then the
 * starter floor — so even a brand-new slot scales with how far the colony has come.
 */
export function slotBaseline(equipped: number, casteGene: number, colonyGene: number): number {
  if (equipped > 0) return equipped;
  if (casteGene > 0) return casteGene;
  if (colonyGene > 0) return colonyGene;
  return STARTER_BASELINE;
}
